import React from "react";
import { Badge, createStyles } from "@mantine/core";

export type BadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "neutral"
  | "replacement"
  | "replaced"
  | "unverified"
  | "wrong_site";

interface StyledBadgeProps {
  variant: BadgeVariant;
  label: string;
}

const useStyles = createStyles((theme) => ({
  badge: {
    fontWeight: 500,
    textTransform: "none",
  },
  success: {
    backgroundColor: "#e8faf5",
    color: "#14926d",
  },
  warning: {
    backgroundColor: "rgba(255, 212, 59, 0.3)",
    color: "#806a1e",
  },
  error: {
    backgroundColor: "rgba(237, 118, 135, 0.2)",
    color: "#ed7687",
  },
  neutral: {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.gray[6],
  },
  replacement: {
    backgroundColor: "rgba(232, 89, 12, 0.15)",
    color: "#e8590c",
  },
  replaced: {
    backgroundColor: "rgba(240, 62, 62, 0.1)",
    color: "#f03e3e",
  },
  // Geofence could not be verified (GPS outside the post radius — drift or an
  // inaccurate/shared post coordinate). The officer is at their assigned site;
  // this is NOT "wrong site" (which is being shown at a post you aren't assigned
  // to), so it carries a softer "Unverified location" label.
  unverified: {
    backgroundColor: "#FFF4EA",
    color: "#D9750C",
  },
  // Reserved for a true site mismatch (clock attributed to a post the officer is
  // not assigned to). Not currently driven off valid_location.
  wrong_site: {
    backgroundColor: "#FFF4EA",
    color: "#FF922B",
  },
}));

const StyledBadge: React.FC<StyledBadgeProps> = ({ variant, label }) => {
  const { classes, cx } = useStyles();

  return (
    <Badge
      size="sm"
      radius="xl"
      className={cx(classes.badge, classes[variant])}
    >
      {label}
    </Badge>
  );
};

export default StyledBadge;
